import { useState, useRef, useEffect, useCallback, useMemo, isValidElement, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { isEmpty, isString } from 'lodash';
import classNames from 'classnames';
import Slider from 'react-slick';
import Banner from '../Banner';
import Helmet from '../Helmet';
import usePlatform from '../../hooks/usePlatform';
import IconAiArrow from '../../components/IconWrapper/arrow-ai';
import { CursorPagination } from '../../components/Common/cursor-pagination';
import { isVideoFile, getBannerOperationExtraAttr } from '../../scripts/utils';
import AIPScene from './aip-scene';
import DifferentiatedAdvantage from './differentiated-advantage';
import Context from '../../container';
import { AIP_PRODUCT_SCHEMAS } from '../../constants/geo';
import Story from './story';
import TrantorStats from './trantor-stats';
import Collaboration from './collaboration';
import AIPCarousel from './aip-carousel';
import { ossPrefix } from './constants';
import './detail.scss';

// 判断列表中的视频是否都是同一个
const isSameVideoSrc = (list: any[]) => {
  const videoSrcs = list.filter(item => item.src && isVideoFile(item.src)).map(item => item.src);
  if (videoSrcs.length === 0) return false;
  return videoSrcs.every(src => src === videoSrcs[0]);
};

// 根据当前播放时间计算应该选中的索引
const getActiveIndexByTime = (currentTime: number, list: any[]) => {
  // 从后往前遍历，找到第一个 start 小于等于当前时间的项
  for (let i = list.length - 1; i >= 0; i--) {
    const start = list[i].start ?? 0;
    if (currentTime >= start) {
      return i;
    }
  }
  return 0;
};

// 判断 children 是否是 React 元素（单个或数组）
const isReactChildren = (children: any): boolean => {
  if (isValidElement(children)) return true;
  if (Array.isArray(children) && children.length > 0 && children.every(isValidElement)) return true;
  return false;
};

// 生成安全的 id，用于锚点定位
const generateId = (title: any, index: number) => {
  return isString(title) ? `section-${index}-${title.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '-')}` : `section-${index}`;
};

const list = [
  {
    title: 'AI管理驾驶舱',
    name: 'aivault',
    desc: '让管理智慧和经验，成为企业永不断供的能力',
    src: `${ossPrefix}ai-cockpit.mp4`,
    mobileSrc: `${ossPrefix}aip-mobile-cockpit.mp4`,
    children: [
      {
        title: '企业的“AI 管理中枢”',
        showSort: true,
        desc: 'AI管理驾驶舱是企业管理的辅助决策中枢，真正让管理软件参与企业的管理决策。它将最有价值的管理\n理论、行业经验和企业实践系统性地植入软件，让具备管理能力的 AI 辅助个人与组织决策，如同水、\n电一样，成为企业永不断供的能力。',
        src: '/images/aip/1-1.png',
        boxes: [
          { top: '200px', left: '150px', size: 600, color: '#0366DD', opacity: 0.6 },
        ],
      },
      {
        title: 'AI Agent驱动的管理决策架构',
        showSort: true,
        desc: 'AI管理驾驶舱通过AI Agent辅助企业管理决策，在AI架构上围绕如下3个核心：\n【理论｜嵌入管理理论】 · 【软件｜驱动软件执行】 · 【监护｜遵循管理规范】',
        src: `${ossPrefix}概论.png`,
        boxes: [
          { top: '140px', left: '0px', size: 418, color: '#379BE3', opacity: 0.3 },
          { top: '950px', left: 'calc(50% - 300px)', size: 500, color: '#5D37E3', opacity: 0.6 },
        ],
        children: [
          {
            title: '嵌入管理理论',
            desc: '以管理理论为先导构建业务场景，包括经典管理理论、公司内部管理实践、咨询公司管理方案等，覆盖企业的全链路业务领域。',
            src: `${ossPrefix}ai-agent-driven.mp4`,
            start: 0,
          },
          {
            title: '驱动软件执行',
            desc: '以管理理论为驱动，形成 “感知–洞察–行动” 的管理闭环。持续感知企业运行状态，对指标异动提出建议，并驱动操作执行。',
            src: `${ossPrefix}ai-agent-driven.mp4`,
            start: 8,
          },
          {
            title: '遵循管理规范',
            desc: '不仅是数字员工，更是专业可靠的管理顾问。通过理论+工具双认证，绑定业务目标，全链路留痕+人类反馈，确保执行规范。',
            src: `${ossPrefix}ai-agent-driven.mp4`,
            start: 28,
          }
        ]
      },
      {
        title: '差异化优势',
        boxes: [
          { top: '535px', left: '20%', size: 240, color: '#379BE3', opacity: 0.6 },
        ],
        children: <DifferentiatedAdvantage />
      },
      {
        title: '真实场景\n即刻体验',
        boxes: [
          { top: '50px', left: 'calc(100% - 520px)', size: 320, color: '#C0DD03', opacity: 0.8 },
          { top: '800px', left: 'calc(100% - 300px)', size: 480, color: '#5D37E3', opacity: 0.8 },
          { top: '1800px', left: '-200px', size: 600, color: '#FFE37E', opacity: 0.9 },
        ],
        children: [<Story key="ai-story" className='aip-section-wrapper'/>, <AIPScene key="aip-scene" />]
      }
    ]
  },
  {
    title: '业务应用',
    desc: '构建全域协同的产业链全景智慧',
    name: 'application',
    src: `${ossPrefix}ai-application.mp4`,
    mobileSrc: `${ossPrefix}ai-mobile-application.mp4`,
    children: [
      {
        title: <>围绕核心企业<br/><span style={{ color: '#0366DD' }}>构建产业互联网</span></>,
        desc: '端点科技“AIP业务应用”将产业链上下游不同角色企业的核心资源进行统一管理，\n构建同时具备纵向管理深度和横向产业协同的新型企业',
        showSort: true,
        boxes: [
          { top: '76px', left: '29%', size: 269, color: '#3790E3', opacity: 0.8 },
          { top: '70%', left: '9%', size: 418, color: '#5D37E3', opacity: 0.3 },
        ],
        children: <AIPCarousel />,
      },
      {
        title: <>同源产品架构<br/><span style={{ color: '#0366DD' }}>核心业务领域</span></>,
        desc: 'AIP沉淀了众多行业企业的业务实践经验，以此构建了覆盖企业资源管理层的十大核心业务模块',
        showSort: true,
        children: <Collaboration />,
      },
      {
        title: <>一个运行平台<br/><span style={{ color: '#0366DD' }}>多元业务协同</span></>,
        desc: 'AIP将企业核心管理模块与上下游伙伴的多元业务门户、一线操作人员的执行端统一运行在一个平台上，\n覆盖全场景业务流程，灵活配置，满足企业多样业务需求，驱动产业链协同发展',
        showSort: true,
        boxes: [
          { top: '70%', left: '12%', size: 418, color: '#379BE3', opacity: 0.5 },
        ],
        children: [
          {
            title: '多元业务门户',
            desc: '提供内外部伙伴使用界面，实现产业链伙伴业务协同。',
            src: `${ossPrefix}伙伴协同.png`,
          },
          {
            title: '1000+核心流程',
            desc: '内置最佳管理业务实践，支撑各管理领域业务流程。',
            src: `${ossPrefix}核心流程.png`,
          },
          {
            title: '3000+配置参数',
            desc: '流程和对象通过参数配置，提高企业业务适配灵活度。',
            src: `${ossPrefix}参数配置.png`,
          }
        ]
      },
      {
        title: '我们的优势',
        boxes: [
          { top: '100px', left: 'calc(100% - 200px)', size: 240, color: '#C0DD03', opacity: 0.6 },
          { top: '30%', left: '0px', size: 700, color: '#FFE37E', opacity: 0.6 },
        ],
        children: <TrantorStats styleCard={{ height: '308px' }} items={[
          { title: '『 全域协同 』', label: '基于统一平台无缝集成企业内外部各类应用，实现企业内部业务、企业上下游和全产业链的生态协同与创新，有效破除生产要素的流动壁垒。', img: '/images/aip/collaboration.png' },
          { title: '『 智慧经营 』', label: '借助强大的数字技术对行业趋势进行全面感知，实现对业务流程的敏捷管理和实时的数据决策。', img: '/images/aip/operate.png' },
          { title: '『 可持续发展 』', label: '依托敏捷可持续可迭代的技术体系，赋予企业持续变革和创新的能力，同时实现绿色、低碳、可持续的循环经济。', img: '/images/aip/development.png' },
        ]} />,
      },
    ]
  },
  {
    title: 'AI智能平台',
    name: 'aifoundation',
    desc: '让企业轻松实现智能自治',
    src: `${ossPrefix}ai-platform.mp4`,
    mobileSrc: `${ossPrefix}aip-mobile-platform.mp4`,
    note: '企业级AI创作平台，支持AI应用的全生命周期管理，从构建、评测、观测到优化，全程可视、实时洞察、一键优化。让每一家企业都能快速构建自己的AI管理驾驶舱，轻松拥有AI管理的超能力。',
    children: [
      {
        title: '软件使用范式重构\n数智员工上岗',
        desc: '以管理逻辑为核心，构建企业级数智员工组织。\n实现实时洞察 · 风险预警 · 行动建议 · 执行闭环的一站式驱动。',
        showSort: true,
        src: '/images/aip/3-1.png',
        boxes: [
          { top: '100px', left: '0', size: 600, color: '#3790E3', opacity: 0.6 },
          { top: '50%', left: 'calc(100% - 400px)', size: 418, color: '#3790E3', opacity: 0.4 },
        ],
      },
      {
        title: '构建属于您的\nAI管理驾驶舱应用场景',
        showSort: true,
        boxes: [
          { top: '0px', left: '-50px', size: 500, color: '#FFE37E', opacity: 0.6 },
        ],
        children: [
          {
            title: '管理理论驱动的系统范式',
            desc: '通过选择管理理论与企业内部制度 · 系统自动构建业务场景的结构与逻辑 · 使管理方式成为软件运行的底层驱动力。',
            src: `${ossPrefix}1、构建属于您的AI 管理驾驶舱应用场景.mp4`,
            start: 0,
          },
          {
            title: '能力组合式的场景构建',
            desc: '数智员工基于场景调用不同管理技能 · 协同完成场景目标 · 使业务执行具备理论驱动、可协作、可适配的能力结构。',
            src: `${ossPrefix}1、构建属于您的AI 管理驾驶舱应用场景.mp4`,
            start: 16,
          },
          {
            title: '兜底风控的可配置管理防线',
            desc: '在数智员工启动之前，系统允许对关键动作配置触发条件与人工确认，构建可控、可审防线，确保运行中的业务安全。',
            src: `${ossPrefix}1、构建属于您的AI 管理驾驶舱应用场景.mp4`,
            start: 23,
          }
        ]
      },
      {
        title: '您的AI\n始终可控',
        showSort: true,
        boxes: [
          { top: '72px', left: 'calc(100% - 679px)', size: 800, color: '#78E147', opacity: 0.6 },
          { top: '0', left: 'calc(100% - 679px)', size: 418, color: '#E39637', opacity: 0.6 },
          { top: '520px', left: '100px', size: 418, color: '#379BE3', opacity: 0.6 },
        ],
        children: [
          {
            title: '多层次安全管控机制',
            desc: '全面的Agent安全管控，包括输入输出控制、上下文控制等，确保AI系统的操作符合企业安全标准。',
            src: `${ossPrefix}2、您的AI始终可控.mp4`,
            start: 4,
          },
          {
            title: '工具与数据安全管理',
            desc: '强化工具安全，支持业务逻辑服务、MCP权限、知识库访问等权限管理，对数智员工进行代理授权与自主授权管理，确保数据安全。',
            src: `${ossPrefix}2、您的AI始终可控.mp4`,
            start: 16,
          },
          {
            title: '场景驱动的数智员工选择与幻觉防控',
            desc: '系统根据业务场景启用对应的数智员工，通过业务逻辑绑定贴合业务的语义，避免偏离实际、不可执行的回答或行动建议。',
            src: `${ossPrefix}2、您的AI始终可控.mp4`,
            start: 23,
          }
        ]
      },
      {
        title: '旧系统\n秒变智能',
        showSort: true,
        boxes: [
          { top: '30px', right: '-50px', size: 600, color: '#5D37E3', opacity: 0.6 },
        ],
        desc: 'AIP不替代企业自有系统，而是连接与扩展；\n在不颠覆企业现有IT架构的前提下，快速实现“智能化改造”的目标。',
        children: [
          {
            title: '多种集成方式支持',
            desc: '支持通过HTTP API、SDK等多种方式与企业现有系统进行无缝集成，确保系统间的数据流畅连接。',
            src: `/images/aip/intelligent-1.png`,
          },
          {
            title: '高效的数据同步与处理',
            desc: '支持通过HTTP 通过数据平台（ETL、ELT、CDC），实现企业数据的实时同步和高效处理，确保数据更新及时准确。',
            src: `/images/aip/intelligent-2.png`,
          },
          {
            title: '多元化与主流系统开箱即用',
            desc: '支持通过HTTP 提供开箱即用的连接器，兼容主流企业系统，快速实现系统集成，支持灵活扩展和个性化定制。',
            src: `/images/aip/out-of-box.png`,
          },
        ]
      }
    ]
  },
  {
    title: 'Trantor软件构建平台',
    name: 'trantor',
    desc: '让智能复杂系统轻松落地',
    src: `${ossPrefix}aip—bg-trantor2.mp4`,
    mobileSrc: `${ossPrefix}aip-mobile-trantor.mp4`,
    note: '企业级业务软件开发平台。通过可视化配置和无代码或少量代码，快速构建复杂业务系统，提升开发效率，降低技术门槛，助力企业实现数字化转型。',
    noteTips: [
      { value: '-50%', label: '应用迭代周期', img: '/images/aip/cycle.png' },
      { value: '-92%', label: '代码行数', img: '/images/aip/code.png' },
      { value: '-60%', label: '开发人员数量', img: '/images/aip/developer.png' }
    ],
    children: [
      {
        title: <><span style={{ color: '#0366DD' }}>模型构建</span><br/>以业务为视角的“乐高积木”</>,
        showSort: true,
        boxes: [
          { top: '32%', right: '-300px', size: 640, color: '#0366DD', opacity: 0.6 },
        ],
        children: [
          {
            title: '全谱系字段',
            desc: '预置文本、数字、枚举等企业场景全谱系字段，精细管控小数、文本和格式，零代码开箱即用。',
            src: `${ossPrefix}1、模型构建 · 以业务为视角的“乐高积木”.mp4`,
            start: 0,
          },
          {
            title: '自动分库分表',
            desc: '内置企业级分库分表策略，海量数据分布式存储，平台可扩展，数据可支撑，有效支撑企业规模化发展。',
            src: `${ossPrefix}1、模型构建 · 以业务为视角的“乐高积木”.mp4`,
            start: 130,
          },
          {
            title: '智能搜索配置',
            desc: '简单配置就能把业务数据实时同步到搜索引擎，企业可轻松拥有电商级的全文检索体验。',
            src: `${ossPrefix}1、模型构建 · 以业务为视角的“乐高积木”.mp4`,
            start: 142,
          },
          {
            title: '可视化关系画布',
            desc: '把抽象的数据关系拖成一张业务拓扑图，订单、客户、物料一目了然，复杂 ERP、CRM 逻辑也能轻松映射。',
            src: `${ossPrefix}1、模型构建 · 以业务为视角的“乐高积木”.mp4`,
            start: 163,
          },
        ]
      },
      {
        title: <><span style={{ color: '#0366DD' }}>服务编排</span><br/>业务逻辑的“可视化装配线”</>,
        showSort: true,
        boxes: [
          { top: '260px', left: '20%', size: 600, color: '#5D37E3', opacity: 0.6 },
        ],
        children: [
          {
            title: '图灵完备的逻辑控制',
            desc: '支持循环、分支、并行等完整的业务运算能力，轻松应对高复杂度业务场景。',
            src: `${ossPrefix}2、服务编排 · 业务逻辑的“可视化装配线”.mp4`,
            start: 0,
          },
          {
            title: '全栈数据操作',
            desc: '内置数据处理能力和业务逻辑处理引擎，配置即用，快速完成全生命周期数据处理。',
            src: `${ossPrefix}2、服务编排 · 业务逻辑的“可视化装配线”.mp4`,
            start: 44,
          },
          {
            title: '原生集成AI能力',
            desc: '将大模型、智能体和知识库封装为标准节点，让AI变成业务系统的原生能力，轻松构建智能化场景。',
            src: `${ossPrefix}2、服务编排 · 业务逻辑的“可视化装配线”.mp4`,
            start: 73,
          },
          {
            title: '无边界混合开发',
            desc: '通过脚本与服务调用灵活扩展，也支持Java代码扩展逻辑，打破低代码边界，兼具高效与原生的灵活性。',
            src: `${ossPrefix}2、服务编排 · 业务逻辑的“可视化装配线”.mp4`,
            start: 100,
          }
        ]
      },
      {
        title: <><span style={{ color: '#0366DD' }}>业务事件</span><br/>面向业务的“流程指挥官”</>,
        showSort: true,
        boxes: [
          { top: '0px', left: '50px', size: 600, color: '#FFD745', opacity: 0.9 },
          { top: '500px', left: '26%', size: 418, color: '#379BE3', opacity: 0.6 },
        ],
        children: [
          {
            title: '统一的业务语义',
            desc: '抽象的单据状态和业务操作，让技术和业务人员统一业务话术。操作一键完成，出错自动回滚，保证业务一致。',
            src: `${ossPrefix}3、业务事件 · 面向业务的“流程指挥官”.mp4`,
            start: 0,
          },
          {
            title: '积木式服务组合',
            desc: '业务能力做成可复用、可扩展，自由组合，灵活编排。既能调用自动服务，也能触发人工审批，让复杂业务简单灵活。',
            src: `${ossPrefix}3、业务事件 · 面向业务的“流程指挥官”.mp4`,
            start: 69,
          },
          {
            title: '智能路由与容错',
            desc: '系统根据业务情况自动选择最佳处理路径，内置异常处理和通知机制，有问题及时响应。',
            src: `${ossPrefix}3、业务事件 · 面向业务的“流程指挥官”.mp4`,
            start: 87,
          }
        ]
      },
      {
        title: <><span style={{ color: '#0366DD' }}>流程挖掘</span><br/>企业业务的深度“CT 扫描”</>,
        showSort: true,
        boxes: [
          { top: '60%', left: 'calc(100% - 590px)', size: 320, color: '#C0DD03', opacity: 0.7 },
          { top: '33%', left: 'calc(100% - 418px)', size: 600, color: '#E39637', opacity: 0.5 },
        ],
        children: [
          {
            title: '真实还原业务全貌',
            desc: '自动生成业务实景，直观展示并行、循环与违规路径。滑动即可调节视图，聚焦主干流程或深挖异常，助力合规与风控。',
            src: '/images/aip/process-mining-1.png',
          },
          {
            title: '多维效能诊断',
            desc: '自动分析耗时，精准定位瓶颈，实现量化洞察与提速。模拟并比对流程表现，识别“黄金流程”并推广，持续提升整体人效。',
            src: '/images/aip/process-mining-2.png',
          }
        ]
      }
    ]
  },
  {
    title: 'Erda企业数字化平台',
    name: 'erda',
    desc: '一站式开源云原生PaaS'
  }
]

interface UlListProps {
  list: any[];
  activeIndex?: number;
  onItemClick?: (index: number, item: any) => void;
  onTimeUpdate?: (currentTime: number) => void;
  startTime?: number;
  direction?: 'horizontal' | 'vertical';
}

const UlList = ({ list, activeIndex = 0, onItemClick, onTimeUpdate, startTime, direction }: UlListProps) => {
  const { isPc } = usePlatform() || {};
  const [mobileIndex, setMobileIndex] = useState(0);
  const sliderRef = useRef<Slider>(null);

  // 当外部 activeIndex 变化时，同步 Slider 位置（移动端）
  useEffect(() => {
    if (!isPc && sliderRef.current && activeIndex !== mobileIndex) {
      sliderRef.current.slickGoTo(activeIndex);
      setMobileIndex(activeIndex);
    }
  }, [activeIndex, isPc, mobileIndex]);

  if (isPc) {
    return <div className='flex3'>
      <ul className={classNames('aip-content-sub-list', { 'vertical': direction === 'vertical' })}>
        {list.map((s: any, idx: number) => {
          return <li
            key={idx}
            className={classNames('aip-content-sub-item', { active: idx === activeIndex })}
            onClick={() => onItemClick?.(idx, s)}
          >
            <div className="aip-content-sub-title">
              {s.title}
              <span className="aip-content-sub-title-icon">#{idx + 1}</span>
            </div>
            {s.desc && <div className="aip-content-sub-desc">{s.desc}</div>}
          </li>
        })}
      </ul>
    </div>
  }
  const settings = {
    infinite: true,
    swipe: true,
    autoplay: false,
    autoplaySpeed: 0,
    slidesToScroll: 1,
    slidesToShow: 1,
    speed: 506,
    cssEase: 'ease-out',
    rows: 1,
    arrows: false,
    beforeChange: (current: number, next: number) => {
      setMobileIndex(next);
      onItemClick?.(next, list[next]);
    },
    centerPadding: '0px'
  };
  return <div className="aip-content-mobile">
    <Slider ref={sliderRef} {...settings}>
      {list.map((s: any, idx: number) => {
        return <div key={idx} className="aip-content-mobile-item">
          <div className='aip-content-sub-wrap'>
            <div className="aip-content-sub-title">
                {s.title}
                <span className="aip-content-sub-title-icon">#{idx + 1}</span>
            </div>
            {s.desc && <div className="aip-content-sub-desc">{s.desc}</div>}
          </div>
          <ContentSource
            src={s.src}
            title={s.title}
            startTime={idx === mobileIndex ? startTime : s.start}
            onTimeUpdate={idx === mobileIndex ? onTimeUpdate : undefined}
          />
        </div>
      })}
    </Slider>
    <div className="aip-content-page">
      <CursorPagination
        count={list.length}
        activeIndex={mobileIndex}
      />
    </div>
  </div>
}

interface ContentSourceProps {
  src: string;
  title: string | React.ReactNode;
  className?: string;
  startTime?: number;
  onTimeUpdate?: (currentTime: number) => void;
  showPureImg?: boolean
}

const ContentSource = ({ src, title, className, startTime, onTimeUpdate, showPureImg = false }: ContentSourceProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevSrcRef = useRef(src);
  const isVideo = isVideoFile(src);
  const hasUserInteracted = useRef(false); // 记录用户是否手动操作过

  // 当 src 变化时，重新加载视频并播放
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return;

    if (src !== prevSrcRef.current) {
      prevSrcRef.current = src;
      // src 变化后，等待视频可以播放时跳转到指定时间并播放
      const handleCanPlay = () => {
        video.currentTime = startTime ?? 0;
        video.play().catch(() => {});
        video.removeEventListener('canplay', handleCanPlay);
      };
      video.addEventListener('canplay', handleCanPlay);
      return () => {
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [src, isVideo, startTime]);

  // 当 startTime 变化时（同一视频），跳转视频进度
  useEffect(() => {
    const newStartTime = startTime ?? 0;
    if (videoRef.current) {
      videoRef.current.currentTime = newStartTime;
    }
  }, [startTime]);

  // 监听视频播放进度
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVideo) return;

    const handleTimeUpdate = () => {
      onTimeUpdate?.(video.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [isVideo, onTimeUpdate]);

  // 使用 Intersection Observer 监听视频可见性，自动播放/暂停
  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container || !isVideo) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // 视频进入可视区域（超过50%可见），自动播放
            if (video.paused) {
              // 如果有 startTime 且视频还没开始播放过，跳转到指定时间
              if (startTime !== undefined && video.currentTime === 0) {
                video.currentTime = startTime;
              }
              video.play().catch(() => {});
            }
          } else {
            // 视频离开可视区域，自动暂停
            if (!video.paused) {
              video.pause();
            }
          }
        });
      },
      {
        threshold: [0, 0.5, 1], // 监听 0%, 50%, 100% 可见度
        rootMargin: '0px'
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [isVideo, startTime]);

  // 播放/暂停切换（用户手动操作）
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    hasUserInteracted.current = true;
    if (video.paused) {
      // 如果有 startTime 且视频还没开始播放过，跳转到指定时间
      if (startTime !== undefined && video.currentTime === 0) {
        video.currentTime = startTime;
      }
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [startTime]);

  if(isVideo) {
    return (
      <div ref={containerRef} className={classNames("aip-content-video-container", className)}>
        <video
          className="aip-content-video"
          preload="metadata"
          ref={videoRef}
          controls={false}
          muted // 自动播放需要静音
          playsInline // iOS 需要
          loop // 循环播放
          src={src} // 直接使用 src 属性，方便切换
        />
        {!isPlaying && (
          <div className="aip-content-play" onClick={togglePlay} />
        )}
      </div>
    )
  }
  return showPureImg ? <img className={classNames("aip-content-img", className)} src={src} /> :<div className={classNames("aip-content-video-container", className)}>
      <img className={classNames("aip-content-img", className)} src={src} />
    </div>
}
const ContentA = ({ info, index }: { info: any, index: number }) => {
  return <div className="aip-content flex-column">
    {info.boxes?.map((box: any, idx: number) => <Box index={index} key={idx} {...box} />)}
    <div className="aip-content-top">
      {info.showSort && <div className="title-wrap-sort">
        0{index + 1}
      </div>}
      <div className='aip-content-title-wrap'>
        <div className="aip-content-title">{info.title}</div>
        {info.desc && <div className="aip-content-desc flex-1">{info.desc}</div>}
      </div>
    </div>
    <ContentSource className="flex5 zIndex3" src={info.src} title={info.title} showPureImg />
  </div>
}

const ContentB = ({ info, index }: { info: any, index: number }) => {
  const children = info.children || [];
  const isSameVideo = useMemo(() => isSameVideoSrc(children), [children]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentVideoSrc, setCurrentVideoSrc] = useState(children[0]?.src || '');
  const [startTime, setStartTime] = useState(children[0]?.start ?? 0);

  // 处理视频播放进度更新
  const handleTimeUpdate = useCallback((currentTime: number) => {
    if (isSameVideo) {
      const newIndex = getActiveIndexByTime(currentTime, children);
      if (newIndex !== activeIndex) {
        setActiveIndex(newIndex);
      }
    }
  }, [isSameVideo, children, activeIndex]);

  // 处理列表项点击
  const handleItemClick = useCallback((idx: number, item: any) => {
    setActiveIndex(idx);
    if (isSameVideo) {
      // 同一视频，跳转到对应时间点
      setStartTime(item.start ?? 0);
    } else {
      // 不同视频，切换视频源
      setCurrentVideoSrc(item.src || '');
      setStartTime(item.start ?? 0);
    }
  }, [isSameVideo]);

  return <div className="aip-content flex-column">
    {info.boxes?.map((box: any, idx: number) => <Box index={index} key={idx} {...box} />)}
    <div className="aip-content-top">
      {info.showSort && <div className="title-wrap-sort">
        0{index + 1}
      </div>}
      <div className='aip-content-title-wrap'>
        <div className="aip-content-title">{info.title}</div>
        {info.desc && <div className="aip-content-desc flex-1">{info.desc}</div>}
      </div>
      {info.src && <ContentSource className='aip-single-source' src={info.src} title={info.title} showPureImg />}
    </div>
    {info.direction === 'vertical' ? (
      <div className="flex-column zIndex3">
        <ContentSource
          className="flex5"
          src={currentVideoSrc}
          title={info.title}
          startTime={startTime}
          onTimeUpdate={handleTimeUpdate}
          showPureImg
        />
        <UlList
          list={children}
          activeIndex={activeIndex}
          onItemClick={handleItemClick}
          onTimeUpdate={handleTimeUpdate}
          startTime={startTime}
          direction={info.direction}
        />
      </div>)  : (
    <div className="flex-row zIndex3">
      <UlList
        list={children}
        activeIndex={activeIndex}
        onItemClick={handleItemClick}
        onTimeUpdate={handleTimeUpdate}
        startTime={startTime}
      />
      <ContentSource
        className="flex5"
        src={currentVideoSrc}
        title={info.title}
        startTime={startTime}
        onTimeUpdate={handleTimeUpdate}
      />
    </div>)}
  </div>
}

const ContentC = ({ info, index }: { info: any, index: number }) => {
  const children = info.children || [];
  const isSameVideo = useMemo(() => isSameVideoSrc(children), [children]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentVideoSrc, setCurrentVideoSrc] = useState(children[0]?.src || '');
  const [startTime, setStartTime] = useState(children[0]?.start ?? 0);

  // 处理视频播放进度更新
  const handleTimeUpdate = useCallback((currentTime: number) => {
    if (isSameVideo) {
      const newIndex = getActiveIndexByTime(currentTime, children);
      if (newIndex !== activeIndex) {
        setActiveIndex(newIndex);
      }
    }
  }, [isSameVideo, children, activeIndex]);

  // 处理列表项点击
  const handleItemClick = useCallback((idx: number, item: any) => {
    setActiveIndex(idx);
    if (isSameVideo) {
      // 同一视频，跳转到对应时间点
      setStartTime(item.start ?? 0);
    } else {
      // 不同视频，切换视频源
      setCurrentVideoSrc(item.src || '');
      setStartTime(item.start ?? 0);
    }
  }, [isSameVideo]);

  return <div className="aip-content flex-column">
    {info.boxes?.map((box: any, idx: number) => <Box index={index} key={idx} {...box} />)}
    <div className="aip-content-top">
      {info.showSort && <div className="title-wrap-sort">
        0{index + 1}
      </div>}
      <div className='aip-content-title-wrap'>
        <div className="aip-content-title">{info.title}</div>
        {info.desc && <div className="aip-content-desc">{info.desc || ''}</div>}
      </div>
    </div>
    <div className="flex-row zIndex3">
      <UlList
        list={children}
        activeIndex={activeIndex}
        onItemClick={handleItemClick}
        onTimeUpdate={handleTimeUpdate}
        startTime={startTime}
      />
      <ContentSource
        className="flex5"
        src={currentVideoSrc}
        title={info.title}
        startTime={startTime}
        onTimeUpdate={handleTimeUpdate}
      />
    </div>
  </div>
}

const ContentElement = ({ info, index, className }: { info: any, index: number, className?: string }) => {
  return <div className={classNames("aip-content flex-column", className)}>
    {info.boxes?.map((box: any, idx: number) => <Box index={index} key={idx} {...box} />)}
    <div className="aip-content-top">
      {info.showSort && <div className="title-wrap-sort">
        0{index + 1}
      </div>}
      <div className='aip-content-title-wrap'>
        <div className="aip-content-title">{info.title}</div>
        {info.desc && <div className="aip-content-desc">{info.desc}</div>}
      </div>
    </div>
    {info.children}
  </div>
}

const ContentMobile = ({ info, index }: { info: any, index: number }) => {
  const children = info.children || [];
  const isSameVideo = useMemo(() => isSameVideoSrc(children), [children]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [startTime, setStartTime] = useState(children[0]?.start ?? 0);

  // 处理视频播放进度更新
  const handleTimeUpdate = useCallback((currentTime: number) => {
    if (isSameVideo) {
      const newIndex = getActiveIndexByTime(currentTime, children);
      if (newIndex !== activeIndex) {
        setActiveIndex(newIndex);
      }
    }
  }, [isSameVideo, children, activeIndex]);

  // 处理列表项点击/滑动
  const handleItemClick = useCallback((idx: number, item: any) => {
    setActiveIndex(idx);
    setStartTime(item.start ?? 0);
  }, []);

  return <div className="aip-content flex-column">
    {info.showSort && <div className="title-wrap-sort">0{index + 1}</div>}
    <div className="aip-content-title-wrap">
      <div className="aip-content-title">{info.title}</div>
      {info.desc && <div className="aip-content-desc">{info.desc}</div>}
    </div>
    {info.src && <ContentSource className={classNames({"aip-single-source": !isEmpty(children)})} src={info.src} title={info.title} showPureImg/>}
    {!isEmpty(children) && (
      <UlList
        list={children}
        activeIndex={activeIndex}
        onItemClick={handleItemClick}
        onTimeUpdate={handleTimeUpdate}
        startTime={startTime}
      />
    )}
  </div>
}

const Box = ({ top, left, right, size, color, opacity }: { top: string, left?: string, right?: string, size: number, color: string, opacity?: number }) => {
  const { isPc } = usePlatform() || {};
  if (!isPc) return null;

  const background = `radial-gradient(circle, ${color}66 0%, ${color}40 25%, ${color}1F 50%, ${color}0A 75%, transparent 100%)`;
  return <div className="aip-detail-box" style={{ background, top, left, right, width: `${size}px`, height: `${size}px`, opacity: opacity || 0.4 }} />
}

export default () => {
  const { isPc } = usePlatform() || {};
  const { id } = useParams<{ id: any }>();
  const item = list.find((item) => item.name === id);
  const structuredData = AIP_PRODUCT_SCHEMAS[id as keyof typeof AIP_PRODUCT_SCHEMAS];
  const contentRef = useRef<HTMLDivElement>(null);
  const { dispatch } = useContext(Context);
  if (!item) return null;

  useEffect(() => {
    if (isPc && isVideoFile(item.src)) {
      dispatch({ theme: 'light' });
    }
  },[item.src])

  return <>
    {structuredData ? (
      <Helmet
        title={`${structuredData.name}-端点科技`}
        description={structuredData.description}
        keywords={`${structuredData.name},AIP,企业智慧管理平台,企业AI平台,端点科技`}
        structuredData={structuredData}
      />
    ) : null}
    <div className='aip-banner-wrap'>
      <Banner
        showAdvantage={false}
        covered
        useArrow={false}
        useDots={false}
        size="large"
        type={'BANNER_TYPE.CAROUSEL'}
        name={item.title}
        height={isPc ? (isVideoFile(item.src) ? 700 : 900) : 600}
        config={{
          src: isPc ? (item.src || '/images/aip/aip-detail-bg.png') : (item.mobileSrc || `${ossPrefix}aip-mobile-platform.png`),
          title: item.title,
          subheading: '',
          legend: item.desc,
          note: item.note,
          noteTips: item.noteTips,
          operation: [
            {
              text: '立即咨询',
              icon: <IconAiArrow />,
              extraAttr: getBannerOperationExtraAttr({ jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' } })
            }
          ]
        }}
      />
    </div>
    <div ref={contentRef} className="aip-content-wrap">
      {item.children?.map((s: any, index: number) => {
        const sectionId = generateId(s.title, index);
        if (isReactChildren(s.children)) {
          return <ContentElement key={sectionId} info={s} index={index} className={s.className} />
        }
        if (!isPc) {
          return <ContentMobile key={sectionId} info={s} index={index}/>
        }
        if (isEmpty(s.children)) {
          return <ContentA key={sectionId} info={s} index={index}/>
        } else if (!isEmpty(s.children) && s.desc) {
          return <ContentB key={sectionId} info={s} index={index}/>
        } else if (!isEmpty(s.children)) {
          return <ContentC key={sectionId} info={s} index={index}/>
        }
        return null;
      })}
    </div>
  </>
}
